/**
 * ExceptionsUsagesPanel - Displays individual exception items and their usages
 */
import React, { useEffect, useRef } from 'react'
import { Button } from "antd"
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTimes, faArrowUpRightFromSquare, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons'
import { useDraggablePanel } from 'hooks/core/useDraggablePanel'

export interface ExceptionItemUsage {
  /** The exception phrase text */
  text: string
  /** Line number where this phrase is defined */
  definitionLine: number
  /** Line numbers where this phrase is used */
  usageLines: number[]
  /** The actual content lines where the phrase appears */
  usageContents: string[]
}

interface ExceptionsUsagesPanelProps {
  /** Array of exception items with their usages */
  exceptionItems: ExceptionItemUsage[]
  /** Callback when a usage line is clicked */
  onLineClick: (lineNumber: number) => void
  /** Callback to close the panel */
  onClose: () => void
  /** Editor container ref for positioning */
  editorContainerRef: React.RefObject<HTMLDivElement | null>
}

export const ExceptionsUsagesPanel: React.FC<ExceptionsUsagesPanelProps> = ({
  exceptionItems,
  onLineClick,
  onClose,
  editorContainerRef,
}) => {
  const panelRef = useRef<HTMLDivElement>(null)
  const { position, isDragging, handleHeaderMouseDown } = useDraggablePanel()

  // Set initial position near the right edge of the editor (only once)
  useEffect(() => {
    if (!panelRef.current || !editorContainerRef.current) return
    if (position.x !== 0 || position.y !== 0) return // Already positioned

    const editorRect = editorContainerRef.current.getBoundingClientRect()
    const panel = panelRef.current
    const panelRect = panel.getBoundingClientRect()

    // Position on the right side of the editor, with some margin
    const initialX = window.innerWidth - panelRect.width - 20
    const initialY = editorRect.top + 20

    // Set position directly on the element for initial positioning
    panel.style.left = `${initialX}px`
    panel.style.top = `${initialY}px`
  }, [editorContainerRef, position.x, position.y])

  // Close panel on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [onClose])

  const totalUsages = exceptionItems.reduce((sum, item) => sum + item.usageLines.length, 0)
  const unusedItems = exceptionItems.filter((item) => item.usageLines.length === 0)

  return (
    <div
      ref={panelRef}
      className="bg-white rounded-lg shadow-2xl z-[1000] pointer-events-auto"
      style={{
        position: 'fixed',
        left: position.x !== 0 ? `${position.x}px` : undefined,
        top: position.y !== 0 ? `${position.y}px` : undefined,
        width: '400px',
        maxHeight: '80vh',
        border: '1px solid #D1D5DB', // gray-300
        cursor: isDragging ? 'grabbing' : undefined,
      }}
    >
      {/* Header */}
      <div
        className="px-4 py-3 rounded-t-lg flex items-center justify-between cursor-grab active:cursor-grabbing"
        style={{ backgroundColor: 'var(--dq-brand)' }}
        onMouseDown={handleHeaderMouseDown}
      >
        <div className="flex items-center gap-2 text-white">
          <FontAwesomeIcon icon={faExclamationTriangle} />
          <span className="font-semibold">Exception Usages</span>
        </div>
        <button
          onClick={onClose}
          className="text-white hover:text-gray-200 transition-colors"
          aria-label="Close exceptions usages panel"
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>
      </div>

      {/* Content */}
      <div className="p-4">
        <div className="text-sm text-gray-600 mb-3">
          {exceptionItems.length} exception{exceptionItems.length !== 1 ? 's' : ''} • {totalUsages} total usage
          {totalUsages !== 1 ? 's' : ''}
          {unusedItems.length > 0 && (
            <span className="ml-2 text-orange-600 font-medium">
              ({unusedItems.length} unused)
            </span>
          )}
        </div>

        <div className="space-y-3 max-h-[calc(80vh-180px)] overflow-y-auto">
          {exceptionItems.map((item, index) => {
            const isUnused = item.usageLines.length === 0

            return (
              <div
                key={index}
                className={`border rounded p-3 ${
                  isUnused ? 'border-orange-300 bg-orange-50' : 'border-gray-200'
                }`}
              >
                {/* Exception phrase header */}
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-800 mb-1 break-words">
                      {item.text}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Button
                        icon={<FontAwesomeIcon icon={faArrowUpRightFromSquare} />}
                        iconPosition="end"
                        onClick={() => onLineClick(item.definitionLine)}
                      >
                        Definition (Line {item.definitionLine})
                      </Button>
                      <span
                        className={`text-xs px-2 py-1 rounded ${
                          isUnused
                            ? 'bg-orange-100 text-orange-700 border border-orange-300'
                            : 'bg-blue-100 text-blue-700 border border-blue-300'
                        }`}
                      >
                        {item.usageLines.length} usage{item.usageLines.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Usage locations */}
                {item.usageLines.length > 0 && (
                  <div className="mt-3 space-y-1">
                    <div className="text-xs text-gray-500 font-medium mb-1">Used at:</div>
                    <div className="space-y-1 max-h-[150px] overflow-y-auto">
                      {item.usageLines.map((lineNum, idx) => (
                        <div
                          key={idx}
                          className="border border-gray-200 rounded p-2 hover:bg-cyan-50 hover:border-cyan-300 transition-all cursor-pointer"
                          onClick={() => onLineClick(lineNum)}
                        >
                          <div className="flex items-start justify-between gap-2 mb-1">
                            <span className="text-xs font-mono bg-gray-100 px-2 py-0.5 rounded">
                              Line {lineNum}
                            </span>
                            <FontAwesomeIcon
                              icon={faArrowUpRightFromSquare}
                              className="text-xs flex-shrink-0"
                              style={{ color: 'var(--dq-brand)' }}
                            />
                          </div>
                          <div className="text-xs font-mono text-gray-700 break-all">
                            {item.usageContents[idx]?.trim() || ''}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Unused warning */}
                {isUnused && (
                  <div className="mt-2 text-xs text-orange-600 flex items-center gap-1">
                    <FontAwesomeIcon icon={faExclamationTriangle} />
                    <span>This exception is not used anywhere</span>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>

      {/* Footer */}
      <div className="px-4 py-2 bg-gray-50 rounded-b-lg border-t border-gray-200">
        <div className="text-xs text-gray-500 flex items-center justify-between">
          <span>Click any usage to navigate</span>
          <span className="font-mono">ESC to close</span>
        </div>
      </div>
    </div>
  )
}
